import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import AppService from '../../services/api/app';
import AgentService from '../../services/api/agents';
import { getEnv } from '../../runtimeEnv';

/* ---------- Styles (kept in this file) ---------- */
const PREVIEW_CSS = `
.pv-overlay { --pv-ink: #1f2433; --pv-muted: #667085; --pv-line: #e4e7ec; --pv-accent: #667085; --pv-accent-2: #98a2b3; --pv-warn: #b45309; --pv-warn-bg: #fef3e2; --pv-card: rgba(255,255,255,0.92);
  position: absolute; inset: 0; z-index: 10; display: flex; align-items: stretch; padding: 0; overflow: hidden; pointer-events: auto;
  background: #f4f5f7;
  opacity: 1; visibility: visible; transition: opacity 0.45s ease, visibility 0s linear 0s; }
.pv-overlay--miss { align-items: center; justify-content: center; padding: 24px; background: #eef0f3; }
.pv-overlay--hidden { opacity: 0; visibility: hidden; pointer-events: none; transition: opacity 0.45s ease, visibility 0s linear 0.45s; }
.pv-stage { position: relative; flex: 1; width: auto; min-width: 0; min-height: 0; display: flex; flex-direction: column; padding: 28px 32px 22px; border-radius: 0; overflow: hidden; color: #1f2433;
  background: linear-gradient(180deg, #ffffff 0%, #f3f4f6 100%); box-shadow: none; isolation: isolate; }
.pv-stage[data-tone="backend"] { background: linear-gradient(180deg, #ffffff 0%, #eceef2 100%); }
.pv-stage[data-tone="api"] { background: linear-gradient(180deg, #fbfbfc 0%, #e8eaee 100%); }
.pv-stage[data-tone="preview"] { background: linear-gradient(180deg, #ffffff 0%, #e4e7ec 100%); }
.pv-orb { position: absolute; border-radius: 50%; filter: blur(2px); pointer-events: none; z-index: 0; }
.pv-orb--a { width: 220px; height: 220px; top: -70px; right: -40px; background: radial-gradient(circle, rgba(255,255,255,0.9), transparent 68%); animation: pv-float 7s ease-in-out infinite; }
.pv-orb--b { width: 160px; height: 160px; bottom: 40px; left: -50px; background: radial-gradient(circle, rgba(210, 214, 220, 0.55), transparent 70%); animation: pv-float 9s ease-in-out infinite reverse; }
@keyframes pv-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(12px); } }
.pv-stage-top, .pv-scene, .pv-copy, .pv-nav { position: relative; z-index: 1; }
.pv-stage-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.pv-name { margin: 0; font-size: 15px; font-weight: 650; letter-spacing: -0.01em; color: #1f2433; overflow-wrap: anywhere; }
.pv-kicker { margin: 4px 0 0; font-size: 12px; color: #667085; }
.pv-chip { flex: none; display: inline-flex; align-items: center; gap: 8px; min-height: 32px; padding: 0 12px; border-radius: 999px; background: #f3f4f6; border: 1px solid #e4e7ec; color: #3f4654; font-size: 12px; font-weight: 650; }
.pv-chip i { width: 7px; height: 7px; border-radius: 50%; background: #98a2b3; box-shadow: 0 0 0 4px rgba(152, 162, 179, 0.22); }
.pv-scene { flex: 1; min-height: 180px; margin-top: 18px; border-radius: 18px; background: #f7f8fa; border: 1px solid #e6e8ee; overflow: hidden; box-shadow: inset 0 1px 0 #fff; }
.pv-scene-bar { display: flex; gap: 6px; align-items: center; height: 36px; padding: 0 14px; background: #eceef2; }
.pv-scene-bar b { width: 8px; height: 8px; border-radius: 50%; background: #c5cad3; }
.pv-scene-bar span { margin-left: 8px; font-size: 12px; font-weight: 650; color: #667085; }
.pv-ui, .pv-nodes, .pv-api, .pv-code, .pv-preview { height: calc(100% - 36px); }
.pv-ui { display: grid; grid-template-columns: 88px 1fr; grid-template-rows: 22px 14px 1fr; gap: 12px; padding: 16px; }
.pv-ui span, .pv-api span, .pv-preview span { display: block; border-radius: 10px; background: #e4e7ec; }
.pv-ui span:first-child { grid-row: span 3; background: #d5d9e0; }
.pv-ui span:nth-child(2) { height: 18px; width: 62%; animation: pv-shimmer 1.8s ease-in-out infinite; }
.pv-ui span:nth-child(3) { height: 12px; }
.pv-ui span:nth-child(4) { height: auto; min-height: 44px; }
.pv-nodes { display: flex; flex-direction: column; justify-content: stretch; gap: 12px; padding: 16px; }
.pv-nodes i { flex: 1; width: auto; height: auto; min-height: 36px; border-radius: 14px; background: #e4e7ec; animation: pv-node 2.4s ease-in-out infinite; }
.pv-nodes i:nth-child(2) { width: auto; height: auto; border-radius: 14px; background: #d5d9e0; animation-delay: 0.2s; }
.pv-nodes i:nth-child(3) { width: 72%; animation-delay: 0.4s; }
@keyframes pv-node { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
.pv-api { display: flex; flex-direction: column; justify-content: center; gap: 10px; padding: 16px 18px; }
.pv-api span:nth-child(1) { height: 14px; width: 46%; }
.pv-api span:nth-child(2) { height: 14px; width: 78%; margin-left: auto; background: #d0d5dd; }
.pv-api span:nth-child(3) { height: 14px; width: 58%; }
.pv-code { display: grid; grid-template-columns: 28px 1fr; align-content: center; gap: 12px 14px; padding: 22px 28px; }
.pv-code i { font-style: normal; font-size: 12px; color: #98a2b3; text-align: right; }
.pv-code span { display: block; height: 10px; border-radius: 99px; background: #d5d9e0; }
.pv-code span:nth-child(2) { width: 46%; }
.pv-code span:nth-child(4) { width: 78%; }
.pv-code span:nth-child(6) { width: 62%; margin-left: 18px; }
.pv-code span:nth-child(8) { width: 70%; margin-left: 18px; }
.pv-code span:nth-child(10) { width: 34%; }
.pv-sk { height: calc(100% - 36px); min-height: 0; box-sizing: border-box; }
.pv-sk-think { display: flex; flex-direction: column; justify-content: center; gap: 16px; padding: 24px 28px; }
.pv-sk-think span { display: block; height: 12px; border-radius: 99px; background: #d8dce3; }
.pv-sk-think span:nth-child(1) { width: 22%; height: 18px; background: #c5cad3; }
.pv-sk-think span:nth-child(2) { width: 88%; }
.pv-sk-think span:nth-child(3) { width: 76%; }
.pv-sk-think span:nth-child(4) { width: 63%; }
.pv-sk-think span:nth-child(5) { width: 41%; animation: pv-shimmer 1.8s ease-in-out infinite; }
.pv-sk-analyze { display: flex; flex-direction: column; gap: 12px; padding: 16px; }
.pv-sk-analyze > div { flex: 1; display: flex; align-items: center; gap: 14px; min-height: 0; padding: 0 16px; border-radius: 14px; background: #fff; border: 1px solid #e4e7ec; }
.pv-sk-analyze b { flex: none; width: 18px; height: 18px; border-radius: 50%; background: #e4e7ec; }
.pv-sk-analyze span { display: block; height: 10px; width: 68%; border-radius: 99px; background: #e4e7ec; }
.pv-sk-analyze .is-on { background: #f3f4f6; }
.pv-sk-analyze .is-on b { background: #98a2b3; box-shadow: inset 0 0 0 4px #fff; }
.pv-sk-analyze .is-on span { width: 48%; background: #d0d5dd; }
.pv-sk-plan { display: flex; align-items: stretch; gap: 12px; padding: 16px; }
.pv-sk-plan > div { flex: 1; display: flex; flex-direction: column; gap: 10px; min-width: 0; padding: 14px; border-radius: 14px; background: #fff; border: 1px solid #e4e7ec; }
.pv-sk-plan b { width: 22px; height: 22px; border-radius: 8px; background: #d0d5dd; }
.pv-sk-plan span { display: block; height: 8px; border-radius: 99px; background: #e4e7ec; }
.pv-sk-plan span:last-child { width: 62%; }
.pv-sk-files { display: flex; flex-direction: column; justify-content: center; gap: 10px; padding: 18px 20px; }
.pv-sk-files > div { display: grid; grid-template-columns: 18px 1fr; align-items: center; gap: 12px; height: 36px; padding: 0 12px; border-radius: 10px; background: #fff; border: 1px solid #e4e7ec; }
.pv-sk-files i { width: 14px; height: 14px; border-radius: 4px; background: #d0d5dd; }
.pv-sk-files span { display: block; height: 8px; border-radius: 99px; background: #e4e7ec; }
.pv-sk-files > div:nth-child(1) span { width: 42%; }
.pv-sk-files > div:nth-child(2) span { width: 68%; }
.pv-sk-files > div:nth-child(3) span { width: 54%; }
.pv-sk-files > div:nth-child(4) span { width: 36%; }
.pv-sk-agents { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; padding: 16px; align-content: center; }
.pv-sk-agents > div { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 16px 8px; border-radius: 14px; background: #fff; border: 1px solid #e4e7ec; }
.pv-sk-agents b { width: 36px; height: 36px; border-radius: 50%; background: #d5d9e0; }
.pv-sk-agents span { display: block; height: 8px; width: 70%; border-radius: 99px; background: #e4e7ec; }
.pv-sk-source { display: grid; grid-template-columns: 42% 1fr; }
.pv-sk-source aside { display: flex; flex-direction: column; justify-content: center; gap: 10px; padding: 16px; background: #eef0f3; }
.pv-sk-source aside span, .pv-sk-source main span { display: block; height: 8px; border-radius: 99px; background: #d5d9e0; }
.pv-sk-source aside span:nth-child(odd) { width: 72%; }
.pv-sk-source aside span:nth-child(even) { width: 54%; margin-left: 12px; }
.pv-sk-source main { display: flex; flex-direction: column; justify-content: center; gap: 12px; padding: 18px; }
.pv-sk-source main span:nth-child(1) { width: 40%; }
.pv-sk-source main span:nth-child(2) { width: 76%; }
.pv-sk-source main span:nth-child(3) { width: 58%; margin-left: 14px; }
.pv-sk-source main span:nth-child(4) { width: 66%; margin-left: 14px; }
.pv-sk-mobile { display: grid; place-items: center; }
.pv-sk-mobile > div { width: 92px; height: 78%; max-height: 220px; padding: 10px 8px; border-radius: 18px; background: #fff; border: 1px solid #d0d5dd; display: flex; flex-direction: column; gap: 8px; }
.pv-sk-mobile i { height: 8px; width: 36%; margin: 0 auto; border-radius: 99px; background: #d0d5dd; }
.pv-sk-mobile span { flex: 1; border-radius: 8px; background: #e4e7ec; }
.pv-sk-gateway { display: grid; grid-template-columns: 1fr 72px 1fr; align-items: center; gap: 12px; padding: 20px 24px; }
.pv-sk-gateway > div { display: flex; flex-direction: column; gap: 10px; }
.pv-sk-gateway span { display: block; height: 28px; border-radius: 10px; background: #e4e7ec; }
.pv-sk-gateway b { width: 72px; height: 72px; border-radius: 18px; background: #d5d9e0; justify-self: center; }
.pv-preview { display: grid; place-items: center; }
.pv-preview span { width: 74%; height: 62%; border-radius: 14px; background: linear-gradient(180deg, #ffffff, #dfe3e8); animation: pv-rise 2.8s ease-in-out infinite; }
@keyframes pv-rise { 0%, 100% { transform: scale(0.96); } 50% { transform: scale(1); } }
@keyframes pv-shimmer { 0% { opacity: 0.45; } 50% { opacity: 1; } 100% { opacity: 0.45; } }
.pv-copy { margin-top: 18px; animation: pv-card-in 0.45s ease; }
@keyframes pv-card-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
.pv-copy h3 { margin: 0; font-size: 30px; line-height: 1.15; font-weight: 650; letter-spacing: -0.03em; color: #1a1d26; }
.pv-copy p { max-width: 46ch; margin: 8px 0 0; font-size: 15px; line-height: 1.5; color: #667085; }
.pv-nav { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: auto; padding-top: 18px; }
.pv-dots { display: flex; gap: 8px; padding: 0; margin: 0; list-style: none; }
.pv-dots button { width: 8px; height: 8px; padding: 0; border: 0; border-radius: 999px; background: #d0d5dd; cursor: pointer; }
.pv-dots button[aria-current="true"] { width: 26px; background: #667085; }
.pv-arrow { display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; border: 1px solid #e4e7ec; border-radius: 14px; color: #3f4654; background: #fff; cursor: pointer; }
.pv-arrow:hover { background: #f3f4f6; }
.pv-arrow:focus-visible, .pv-dots button:focus-visible, .pv-retry:focus-visible { outline: 2px solid #98a2b3; outline-offset: 2px; }

.pv-miss { width: min(460px, 100%); padding: 28px 28px 24px; text-align: center; border-radius: 28px; background: rgba(255,255,255,0.9); border: 1px solid rgba(255,255,255,0.7);
  box-shadow: 0 24px 60px rgba(31, 36, 51, 0.12); backdrop-filter: blur(16px); animation: pv-card-in 0.35s ease; }
.pv-miss-art { display: grid; place-items: center; height: 148px; margin-bottom: 8px; border-radius: 20px; background: linear-gradient(160deg, #f4f5f7, #eceef2 60%, #e4e7ec); }
.pv-miss-window { width: 168px; padding: 10px; border-radius: 14px; background: #fff; box-shadow: 0 16px 30px rgba(31, 36, 51, 0.08); }
.pv-miss-bar { display: flex; gap: 5px; margin-bottom: 10px; }
.pv-miss-bar i { width: 7px; height: 7px; border-radius: 50%; background: #d9dce3; }
.pv-miss-bar i:first-child { background: #b8bec8; }
.pv-miss-lines { display: grid; gap: 7px; }
.pv-miss-lines b { display: block; height: 8px; border-radius: 99px; background: linear-gradient(90deg, #e6e8f5, #f4f5fb); }
.pv-miss-lines b:nth-child(1) { width: 70%; }
.pv-miss-lines b:nth-child(3) { width: 48%; }
.pv-miss-kicker { margin: 16px 0 0; font-size: 12px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--pv-accent); }
.pv-headline { margin: 6px 0 0; font-size: 26px; line-height: 1.2; font-weight: 650; letter-spacing: -0.03em; color: var(--pv-ink); }
.pv-sub { margin: 8px auto 0; max-width: 36ch; font-size: 14px; line-height: 1.55; color: var(--pv-muted); }
.pv-actions { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; margin-top: 22px; }
.pv-retry { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 44px; padding: 0 18px; border: 0; border-radius: 999px; font: inherit; font-size: 14px; font-weight: 650; color: #fff; background: linear-gradient(180deg, #2a3148, #1f2433); cursor: pointer; box-shadow: 0 10px 20px rgba(31, 36, 51, 0.16); }
.pv-retry:hover { transform: translateY(-1px); }
.pv-retry:active { transform: scale(0.98); }
.pv-retry--ghost { color: var(--pv-ink); background: #fff; border: 1px solid var(--pv-line); box-shadow: none; }
.pv-retry--ghost:hover { background: #f7f8fc; }

.pv-root.app-container { height: 100%; min-height: 100%; }
.pv-root .main-panel, .pv-root .content-area, .pv-root .preview-container { flex: 1; height: 100%; min-height: 0; }
.pv-root .preview-iframe { width: 100%; height: 100%; }

@media (max-width: 720px) {
  .pv-overlay--miss { padding: 16px; }
  .pv-stage { min-height: 0; padding: 16px; border-radius: 0; }
  .pv-copy h3, .pv-headline { font-size: 24px; }
  .pv-scene { min-height: 140px; }
  .pv-stage-top { flex-direction: column; }
  .pv-sk-plan, .pv-sk-gateway { flex-direction: column; }
  .pv-sk-agents, .pv-sk-source, .pv-sk-gateway { grid-template-columns: 1fr; }
  .pv-sk-gateway b { width: 48px; height: 48px; }
}

@media (prefers-reduced-motion: reduce) {
  .pv-overlay, .pv-overlay--hidden, .pv-copy, .pv-miss, .pv-orb, .pv-nodes i, .pv-ui span, .pv-preview span { transition: none; animation: none; }
}
`;

export const MAX_HEALTH_ATTEMPTS = 10;
export const HEALTH_RETRY_DELAY_MS = 4000;
const REQUEST_TIMEOUT_MS = 8000;
const IFRAME_LOAD_TIMEOUT_MS = 20000;
const GENERATION_POLL_MS = 4000;
const STATUS_MISS_LIMIT = 3;
const CARD_ROTATE_MS = 5500;
const THUMBNAIL_DELAY_MS = 10000;
const HEALTH_URL_FALLBACK = 'https://id-preview-%s-health.app-dev-3.superagentx.ai/health';

const GENERATION_ACTIVE = new Set(['preparing', 'processing', 'streaming', 'creating', 'pending', 'queued']);
const GENERATION_READY = new Set(['success', 'finished', 'completed', 'done', 'restored', 'running']);
const GENERATION_FAILED = new Set(['failed', 'failure', 'error', 'cancelled', 'canceled', 'timeout']);

export const INFO_CARDS = [
  {
    key: 'ui',
    tone: 'ui',
    title: 'UI generation',
    body: 'The application view is loading into this preview.',
  },
  {
    key: 'agents',
    tone: 'backend',
    title: 'Agents',
    body: 'Agents for this app are prepared alongside the interface.',
  },
  {
    key: 'backend',
    tone: 'backend',
    title: 'Backend initialization',
    body: 'The preview host is reaching the services for this project.',
  },
  {
    key: 'api',
    tone: 'api',
    title: 'API integration',
    body: 'Connections are checked before the application is shown.',
  },
  {
    key: 'source',
    tone: 'api',
    title: 'Source view',
    body: 'Project files stay available while the preview loads.',
  },
  {
    key: 'mobile',
    tone: 'preview',
    title: 'Mobile view',
    body: 'The same app is laid out for a smaller screen.',
  },
  {
    key: 'gateway',
    tone: 'ui',
    title: 'Gateway',
    body: 'The gateway that reaches your services is being prepared.',
  },
  {
    key: 'preview',
    tone: 'preview',
    title: 'Preview preparation',
    body: 'Your app appears here as soon as the preview is ready.',
  },
];

export const STREAM_CARDS = [
  {
    key: 'think',
    tone: 'ui',
    title: 'Thinking',
    body: 'Reading the request and deciding what to change.',
  },
  {
    key: 'plan',
    tone: 'backend',
    title: 'Planning',
    body: 'Outlining the screens and changes for this request.',
  },
  {
    key: 'code',
    tone: 'api',
    title: 'Writing code',
    body: 'Updating screens, files, and logic in the project.',
  },
  {
    key: 'files',
    tone: 'preview',
    title: 'Saving files',
    body: 'Writing the updates into the project files.',
  },
  {
    key: 'analyze',
    tone: 'ui',
    title: 'Analyzing',
    body: 'Checking the result before the preview comes back.',
  },
];

const initialSession = {
  status: 'loading',
  failureKind: null,
  attempt: 0,
  healthPhase: 'idle',
  liveStatus: '',
  lastHealthIssue: '',
  statusNote: '',
  cardIndex: 0,
  readyPreviewUrl: null,
};

function sessionReducer(state, action) {
  switch (action.type) {
    case 'reset':
      return {
        ...initialSession,
        liveStatus: action.liveStatus || '',
      };
    case 'patch':
      return { ...state, ...action.patch };
    case 'next-card':
      return { ...state, cardIndex: state.cardIndex + 1 };
    case 'prev-card':
      return { ...state, cardIndex: state.cardIndex - 1 };
    case 'goto-card':
      return { ...state, cardIndex: action.index };
    default:
      return state;
  }
}

export function generationPhase(status) {
  const key = String(status || '').trim().toLowerCase();
  if (!key) return 'unknown';
  if (GENERATION_ACTIVE.has(key)) return 'active';
  if (GENERATION_FAILED.has(key)) return 'failed';
  if (GENERATION_READY.has(key)) return 'ready';
  return 'unknown';
}

export function isHealthyStatusPayload(body) {
  return Boolean(body) && typeof body === 'object' && !Array.isArray(body) && body.status === 'healthy';
}

export function isLoopbackPreview(url) {
  try {
    const { hostname } = new URL(url);
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]' || hostname === '::1';
  } catch {
    return false;
  }
}

export function buildPreviewHealthUrl(projectId, template) {
  const id = String(projectId ?? '').trim();
  if (!id || /[/\s?#]/.test(id)) return null;
  const source = (template !== undefined ? template : getEnv('REACT_APP_PREVIEW_UI_TEMPLATE') || '').trim();
  let healthTemplate = HEALTH_URL_FALLBACK;
  if (source.includes('%s-ui')) {
    healthTemplate = `${source.replace('%s-ui', '%s-health').replace(/\/+$/, '')}/health`;
  }
  return healthTemplate.split('%s').join(id);
}

function environmentLabel(environmentType, previewUrl) {
  if (isLoopbackPreview(previewUrl)) return 'Local preview';
  const key = String(environmentType || 'K8S').toUpperCase();
  if (key === 'K8S') return 'Hosted preview';
  return key;
}

function statusLine(session, localOpening) {
  if (localOpening) return 'Opening local preview';
  if (session.healthPhase === 'opening') return 'Opening preview';
  if (session.healthPhase === 'checking' || session.healthPhase === 'waiting') return 'Connecting to preview';
  if (session.statusNote) return 'Checking the build';
  const phase = generationPhase(session.liveStatus);
  if (phase === 'active') return 'Streaming';
  if (phase === 'failed') return 'Build needs attention';
  return 'Preparing preview';
}

function IframeComponent({
  onIframeRef,
  previewUrl,
  id,
  projectId,
  projectName,
  projectStatus,
  environmentType,
  isEditEnabled,
  highlightBox,
  updateParentState,
  onPreviewReady,
  chatStreaming = false,
}) {
  const [session, dispatch] = useReducer(sessionReducer, initialSession);
  const [iframeRect, setIframeRect] = useState(null);

  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const isEditEnabledRef = useRef(isEditEnabled);
  const updateParentStateRef = useRef(updateParentState);
  const onPreviewReadyRef = useRef(onPreviewReady);
  const previewUrlRef = useRef(previewUrl);
  const projectIdRef = useRef(projectId);
  const projectStatusRef = useRef(projectStatus);
  const retryTimerRef = useRef(null);
  const iframeLoadTimerRef = useRef(null);
  const thumbnailTimerRef = useRef(null);
  const generationTimerRef = useRef(null);
  const readinessControllerRef = useRef(null);
  const previewReadyRef = useRef(false);
  const hostHealthyRef = useRef(false);
  const iframeLoadedRef = useRef(false);
  const attemptRef = useRef(0);
  const runIdRef = useRef(0);
  const healthStartedRef = useRef(false);
  const generationTicketRef = useRef(0);
  const statusMissRef = useRef(0);
  const mountedRef = useRef(true);
  const cardPausedRef = useRef(false);

  const isLoading = session.status !== 'ready' || Boolean(chatStreaming);
  const isFailed = session.status === 'failed' && !chatStreaming;
  const iframeSrc = session.readyPreviewUrl && session.readyPreviewUrl === previewUrl
    ? session.readyPreviewUrl
    : 'about:blank';
  const generationStreaming = generationPhase(session.liveStatus) === 'active'
    && session.healthPhase !== 'checking'
    && session.healthPhase !== 'waiting'
    && session.healthPhase !== 'opening';
  const streaming = Boolean(chatStreaming) || generationStreaming;
  const cards = streaming ? STREAM_CARDS : INFO_CARDS;
  const cardIndex = ((session.cardIndex % cards.length) + cards.length) % cards.length;
  const activeCard = cards[cardIndex];

  useEffect(() => { isEditEnabledRef.current = isEditEnabled; }, [isEditEnabled]);
  useEffect(() => { updateParentStateRef.current = updateParentState; }, [updateParentState]);
  useEffect(() => { onPreviewReadyRef.current = onPreviewReady; }, [onPreviewReady]);
  useEffect(() => { previewUrlRef.current = previewUrl; }, [previewUrl]);
  useEffect(() => { projectIdRef.current = projectId; }, [projectId]);
  useEffect(() => { projectStatusRef.current = projectStatus; }, [projectStatus]);

  useEffect(() => {
    if (onIframeRef && typeof onIframeRef === 'function') {
      onIframeRef(iframeRef.current);
    } else if (onIframeRef && onIframeRef.current !== undefined) {
      onIframeRef.current = iframeRef.current;
    }
  }, [onIframeRef, previewUrl, isLoading]);

  useEffect(() => {
    if (session.status !== 'loading') return undefined;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }
    const interval = setInterval(() => {
      if (!cardPausedRef.current) dispatch({ type: 'next-card' });
    }, CARD_ROTATE_MS);
    return () => clearInterval(interval);
  }, [session.status, projectId, previewUrl]);

  const getIframeTargetOrigin = useCallback(() => {
    const src = iframeRef.current?.src;
    if (!src || src.startsWith('about:')) return null;
    try {
      const origin = new URL(src).origin;
      return origin === 'null' ? null : origin;
    } catch {
      return null;
    }
  }, []);

  const postToPreview = useCallback((message, originOverride) => {
    if (!iframeLoadedRef.current) return;
    const target = iframeRef.current?.contentWindow;
    const origin = originOverride || getIframeTargetOrigin();
    if (!target || !origin) return;
    try {
      target.postMessage(message, origin);
    } catch {
      // ignore: the preview window is not reachable right now
    }
  }, [getIframeTargetOrigin]);

  const sendDesignModeEnabled = useCallback(() => postToPreview({ type: 'DESIGN_MODE_ENABLED' }), [postToPreview]);
  const sendDesignModeDisabled = useCallback(() => postToPreview({ type: 'DESIGN_MODE_DISABLED' }), [postToPreview]);

  useEffect(() => {
    if (isEditEnabled !== true) {
      sendDesignModeDisabled();
      return undefined;
    }

    sendDesignModeEnabled();
    const t1 = setTimeout(sendDesignModeEnabled, 300);
    const t2 = setTimeout(sendDesignModeEnabled, 1000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isEditEnabled, sendDesignModeEnabled, sendDesignModeDisabled]);

  const updateIframeRect = () => {
    if (iframeRef.current && containerRef.current) {
      const iframeR = iframeRef.current.getBoundingClientRect();
      const containerR = containerRef.current.getBoundingClientRect();
      setIframeRect({
        left: iframeR.left - containerR.left + containerRef.current.scrollLeft,
        top: iframeR.top - containerR.top + containerRef.current.scrollTop,
        width: iframeR.width,
        height: iframeR.height,
      });
    }
  };

  const takeThumbnail = () => {
    const currentProjectId = projectIdRef.current;
    if (!currentProjectId) return;
    AppService.captureThumbnail(currentProjectId)
      .then(() => {})
      .catch((e) => {
        console.error('Failed to capture thumbnail', e);
      });
  };

  const clearTimers = useCallback(() => {
    clearTimeout(retryTimerRef.current);
    clearTimeout(iframeLoadTimerRef.current);
    clearTimeout(thumbnailTimerRef.current);
    clearTimeout(generationTimerRef.current);
    retryTimerRef.current = null;
    iframeLoadTimerRef.current = null;
    thumbnailTimerRef.current = null;
    generationTimerRef.current = null;
    if (readinessControllerRef.current) {
      readinessControllerRef.current.abort();
      readinessControllerRef.current = null;
    }
  }, []);

  const markFailed = useCallback((kind, runId = runIdRef.current) => {
    if (runId !== runIdRef.current) return;
    clearTimers();
    if (kind !== 'iframe') hostHealthyRef.current = false;
    previewReadyRef.current = false;
    iframeLoadedRef.current = false;
    healthStartedRef.current = false;
    dispatch({
      type: 'patch',
      patch: {
        status: 'failed',
        failureKind: kind,
        readyPreviewUrl: null,
        healthPhase: 'idle',
      },
    });
    onPreviewReadyRef.current?.(false);
    updateParentStateRef.current?.({ enableEdit: false });
  }, [clearTimers]);

  const armIframeTimeout = useCallback((runId) => {
    clearTimeout(iframeLoadTimerRef.current);
    iframeLoadTimerRef.current = setTimeout(() => {
      if (runId === runIdRef.current) markFailed('iframe', runId);
    }, IFRAME_LOAD_TIMEOUT_MS);
  }, [markFailed]);

  const openPreview = useCallback((runId) => {
    if (runId !== runIdRef.current) return;
    const nextUrl = previewUrlRef.current;
    if (!nextUrl) {
      markFailed('config', runId);
      return;
    }
    hostHealthyRef.current = true;
    previewReadyRef.current = true;
    dispatch({
      type: 'patch',
      patch: {
        readyPreviewUrl: nextUrl,
        healthPhase: 'opening',
        status: 'loading',
        failureKind: null,
      },
    });
    armIframeTimeout(runId);
  }, [armIframeTimeout, markFailed]);

  const checkPreviewReadiness = useCallback(async (runId) => {
    if (runId !== runIdRef.current) return;
    if (readinessControllerRef.current) return;
    if (!projectIdRef.current || !previewUrlRef.current) {
      markFailed('config', runId);
      return;
    }
    if (attemptRef.current >= MAX_HEALTH_ATTEMPTS) {
      markFailed('health', runId);
      return;
    }

    attemptRef.current += 1;
    const currentAttempt = attemptRef.current;
    dispatch({
      type: 'patch',
      patch: { attempt: currentAttempt, healthPhase: 'checking', status: 'loading', failureKind: null },
    });

    const healthUrl = buildPreviewHealthUrl(projectIdRef.current);
    if (!healthUrl) {
      dispatch({ type: 'patch', patch: { lastHealthIssue: 'Invalid project id' } });
      markFailed('health', runId);
      return;
    }

    const controller = new AbortController();
    readinessControllerRef.current = controller;
    let timedOut = false;
    const abortTimer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, REQUEST_TIMEOUT_MS);

    const retryOrFail = (issue) => {
      if (runId !== runIdRef.current) return;
      dispatch({ type: 'patch', patch: { lastHealthIssue: issue } });
      if (currentAttempt >= MAX_HEALTH_ATTEMPTS) {
        markFailed('health', runId);
        return;
      }
      dispatch({ type: 'patch', patch: { healthPhase: 'waiting' } });
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        checkPreviewReadiness(runId);
      }, HEALTH_RETRY_DELAY_MS);
    };

    try {
      const response = await fetch(healthUrl, {
        method: 'GET',
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (runId !== runIdRef.current) return;

      if (!response.ok) {
        retryOrFail(response.status === 404 ? 'HTTP 404' : `HTTP ${response.status}`);
        return;
      }

      let body = null;
      try {
        body = await response.json();
      } catch {
        if (runId !== runIdRef.current) return;
        retryOrFail('Invalid response');
        return;
      }
      if (runId !== runIdRef.current) return;

      if (isHealthyStatusPayload(body)) {
        openPreview(runId);
        return;
      }
      retryOrFail('Status was not healthy');
    } catch (error) {
      if (runId !== runIdRef.current) return;
      if (error?.name === 'AbortError' && !timedOut) return;
      retryOrFail(timedOut ? 'Timed out' : 'Network error');
    } finally {
      clearTimeout(abortTimer);
      if (readinessControllerRef.current === controller) readinessControllerRef.current = null;
    }
  }, [markFailed, openPreview]);

  const beginHealthChecks = useCallback((runId) => {
    if (runId !== runIdRef.current || healthStartedRef.current) return;
    clearTimeout(generationTimerRef.current);
    generationTimerRef.current = null;
    healthStartedRef.current = true;
    attemptRef.current = 0;

    if (isLoopbackPreview(previewUrlRef.current)) {
      openPreview(runId);
      return;
    }
    checkPreviewReadiness(runId);
  }, [checkPreviewReadiness, openPreview]);

  const pollGeneration = useCallback(async (runId) => {
    if (runId !== runIdRef.current || healthStartedRef.current) return;
    const ticket = generationTicketRef.current + 1;
    generationTicketRef.current = ticket;
    const currentProjectId = projectIdRef.current;
    if (!currentProjectId) {
      markFailed('config', runId);
      return;
    }

    const schedule = () => {
      if (runId !== runIdRef.current || ticket !== generationTicketRef.current || healthStartedRef.current) return;
      generationTimerRef.current = setTimeout(() => {
        generationTimerRef.current = null;
        pollGeneration(runId);
      }, GENERATION_POLL_MS);
    };

    try {
      const response = await AgentService.getProjectById(currentProjectId);
      if (runId !== runIdRef.current || ticket !== generationTicketRef.current || healthStartedRef.current) return;
      const nextStatus = response?.projectDTO?.projectStatus ?? response?.projectStatus ?? '';
      if (!nextStatus) {
        statusMissRef.current += 1;
        if (statusMissRef.current >= STATUS_MISS_LIMIT) {
          dispatch({
            type: 'patch',
            patch: { statusNote: 'Generation status could not be refreshed' },
          });
          beginHealthChecks(runId);
          return;
        }
        schedule();
        return;
      }

      statusMissRef.current = 0;
      dispatch({ type: 'patch', patch: { liveStatus: nextStatus, statusNote: '' } });
      const phase = generationPhase(nextStatus);
      if (phase === 'failed') {
        markFailed('generation', runId);
        return;
      }
      if (phase === 'ready') {
        beginHealthChecks(runId);
        return;
      }
      if (phase === 'unknown') {
        beginHealthChecks(runId);
        return;
      }
      schedule();
    } catch {
      if (runId !== runIdRef.current || ticket !== generationTicketRef.current || healthStartedRef.current) return;
      statusMissRef.current += 1;
      if (statusMissRef.current >= STATUS_MISS_LIMIT) {
        dispatch({
          type: 'patch',
          patch: { statusNote: 'Generation status could not be refreshed' },
        });
        beginHealthChecks(runId);
        return;
      }
      schedule();
    }
  }, [beginHealthChecks, markFailed]);

  const startCycle = useCallback(() => {
    clearTimers();
    generationTicketRef.current += 1;
    runIdRef.current += 1;
    const runId = runIdRef.current;
    attemptRef.current = 0;
    statusMissRef.current = 0;
    healthStartedRef.current = false;
    hostHealthyRef.current = false;
    previewReadyRef.current = false;
    iframeLoadedRef.current = false;
    dispatch({ type: 'reset', liveStatus: projectStatusRef.current || '' });
    onPreviewReadyRef.current?.(false);
    updateParentStateRef.current?.({ enableEdit: false });

    if (!previewUrlRef.current || !projectIdRef.current) {
      markFailed('config', runId);
      return;
    }

    const phase = generationPhase(projectStatusRef.current);
    if (phase === 'failed') {
      markFailed('generation', runId);
      return;
    }
    if (phase === 'ready') {
      beginHealthChecks(runId);
      return;
    }
    pollGeneration(runId);
  }, [beginHealthChecks, clearTimers, markFailed, pollGeneration]);

  useEffect(() => {
    mountedRef.current = true;
    startCycle();
    return () => {
      mountedRef.current = false;
      runIdRef.current += 1;
      generationTicketRef.current += 1;
      clearTimers();
    };
  }, [projectId, previewUrl, startCycle, clearTimers]);

  useEffect(() => {
    projectStatusRef.current = projectStatus;
    if (!projectIdRef.current || !previewUrlRef.current) return;
    const phase = generationPhase(projectStatus);
    if (phase === 'failed') {
      markFailed('generation');
      return;
    }
    if (!projectStatus) return;
    dispatch({ type: 'patch', patch: { liveStatus: projectStatus, statusNote: '' } });
    if (phase === 'ready' || phase === 'unknown') {
      beginHealthChecks(runIdRef.current);
    }
  }, [projectStatus, beginHealthChecks, markFailed]);

  const retryPreview = useCallback(() => {
    if (generationPhase(projectStatusRef.current) === 'failed') {
      const previous = projectStatusRef.current;
      projectStatusRef.current = '';
      startCycle();
      projectStatusRef.current = previous;
      return;
    }
    startCycle();
  }, [startCycle]);

  const refreshIframe = useCallback(() => {
    const runId = runIdRef.current;
    if (!hostHealthyRef.current || !previewUrlRef.current) {
      startCycle();
      return;
    }
    iframeLoadedRef.current = false;
    dispatch({
      type: 'patch',
      patch: { status: 'loading', failureKind: null, readyPreviewUrl: null, healthPhase: 'opening' },
    });
    setTimeout(() => {
      if (runId !== runIdRef.current || !previewUrlRef.current) return;
      previewReadyRef.current = true;
      dispatch({
        type: 'patch',
        patch: { readyPreviewUrl: previewUrlRef.current, healthPhase: 'opening', status: 'loading' },
      });
      armIframeTimeout(runId);
    }, 0);
  }, [armIframeTimeout, startCycle]);

  const handleIframeLoad = () => {
    if (!previewReadyRef.current) return;
    if (!iframeRef.current?.src || iframeRef.current.src.startsWith('about:blank')) return;

    setTimeout(() => {
      if (mountedRef.current) updateIframeRect();
    }, 0);

    clearTimeout(iframeLoadTimerRef.current);
    iframeLoadTimerRef.current = null;
    iframeLoadedRef.current = true;

    dispatch({ type: 'patch', patch: { status: 'ready', failureKind: null } });
    onPreviewReadyRef.current?.(true);
    updateParentStateRef.current?.({ enableEdit: true });

    if (isEditEnabledRef.current) {
      sendDesignModeEnabled();
      setTimeout(sendDesignModeEnabled, 300);
    }

    postToPreview({ type: 'IFRAME_READY', parentOrigin: window.location.origin, origin: window.location.origin }, '*');

    clearTimeout(thumbnailTimerRef.current);
    thumbnailTimerRef.current = setTimeout(takeThumbnail, THUMBNAIL_DELAY_MS);
  };

  useEffect(() => {
    const handleResize = () => updateIframeRect();
    window.addEventListener('resize', handleResize);
    if (highlightBox) updateIframeRect();
    return () => window.removeEventListener('resize', handleResize);
  }, [highlightBox]);

  const showCard = (index) => {
    cardPausedRef.current = true;
    dispatch({ type: 'goto-card', index });
  };

  const failureCopy = () => {
    if (session.failureKind === 'generation') {
      return {
        title: 'The build didn\'t finish',
        body: 'This preview stays closed until the project build succeeds. You can check again.',
      };
    }
    if (session.failureKind === 'iframe') {
      return {
        title: 'The app didn\'t open',
        body: 'The preview was ready, but the application didn\'t finish loading.',
      };
    }
    if (session.failureKind === 'config') {
      return {
        title: 'No preview yet',
        body: 'This project doesn\'t have a preview address yet.',
      };
    }
    return {
      title: 'Preview isn\'t ready',
      body: 'We couldn\'t open this application yet. Try again whenever you\'re ready.',
    };
  };

  const overlayClass = ['pv-overlay', isFailed && 'pv-overlay--miss', !isLoading && 'pv-overlay--hidden'].filter(Boolean).join(' ');
  const failure = failureCopy();
  const localOpening = isLoopbackPreview(previewUrl) && session.healthPhase === 'opening';
  const liveStatus = statusLine(session, localOpening);

  return (
    <div className="app-container pv-root">
      <style>{PREVIEW_CSS}</style>
      <main className="main-panel">
        <div className="content-area">
          <div ref={containerRef} className="preview-container" style={{ margin: 0, position: 'relative' }}>
            <iframe
              ref={iframeRef}
              src={iframeSrc}
              id={id}
              className="preview-iframe"
              title="Preview"
              onLoad={handleIframeLoad}
              onError={() => markFailed('iframe')}
            />

            <div className={overlayClass} data-testid="preview-overlay" data-state={session.status}>
              {isFailed ? (
                <div className="pv-miss">
                  <div className="pv-miss-art" aria-hidden="true">
                    <div className="pv-miss-window">
                      <div className="pv-miss-bar"><i /><i /><i /></div>
                      <div className="pv-miss-lines"><b /><b /><b /></div>
                    </div>
                  </div>
                  <p className="pv-miss-kicker">{projectName || 'Untitled project'}</p>
                  <h2 className="pv-headline">{failure.title}</h2>
                  <p className="pv-sub">{failure.body}</p>
                  <div className="pv-actions">
                    {session.failureKind === 'iframe' && (
                      <button type="button" className="pv-retry pv-retry--ghost" onClick={refreshIframe}>
                        Refresh
                      </button>
                    )}
                    <button type="button" className="pv-retry" onClick={retryPreview}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M20 12a8 8 0 1 1-2.6-5.9M20 4v5h-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Try again
                    </button>
                  </div>
                </div>
              ) : (
                <section
                  className="pv-stage"
                  data-tone={activeCard.tone}
                  aria-roledescription="carousel"
                  aria-label="Highlights while your preview starts"
                  onMouseEnter={() => { cardPausedRef.current = true; }}
                  onMouseLeave={() => { cardPausedRef.current = false; }}
                  onFocus={() => { cardPausedRef.current = true; }}
                  onBlur={() => { cardPausedRef.current = false; }}
                >
                  <div className="pv-orb pv-orb--a" aria-hidden="true" />
                  <div className="pv-orb pv-orb--b" aria-hidden="true" />
                  <div className="pv-stage-top">
                    <div>
                      <h2 className="pv-name">{projectName || 'Untitled project'}</h2>
                      <p className="pv-kicker">{environmentLabel(environmentType, previewUrl)} · {streaming ? 'Generating your app' : 'Loading preview'}</p>
                    </div>
                    <div className="pv-chip" data-testid="preview-status">
                      <i aria-hidden="true" />
                      <span data-testid="preview-generation-status">{liveStatus}</span>
                    </div>
                  </div>

                  <div className="pv-scene" aria-hidden="true" key={activeCard.key}>
                    <div className="pv-scene-bar"><b /><b /><b /><span>{activeCard.title}</span></div>
                    {activeCard.key === 'think' && (
                      <div className="pv-sk pv-sk-think"><span /><span /><span /><span /><span /></div>
                    )}
                    {activeCard.key === 'plan' && (
                      <div className="pv-sk pv-sk-plan">
                        <div><b /><span /><span /></div>
                        <div><b /><span /><span /></div>
                        <div><b /><span /><span /></div>
                      </div>
                    )}
                    {activeCard.key === 'code' && (
                      <div className="pv-code">
                        <i>1</i><span /><i>2</i><span /><i>3</i><span /><i>4</i><span /><i>5</i><span />
                      </div>
                    )}
                    {activeCard.key === 'files' && (
                      <div className="pv-sk pv-sk-files">
                        <div><i /><span /></div>
                        <div><i /><span /></div>
                        <div><i /><span /></div>
                        <div><i /><span /></div>
                      </div>
                    )}
                    {activeCard.key === 'analyze' && (
                      <div className="pv-sk pv-sk-analyze">
                        <div><b /><span /></div>
                        <div className="is-on"><b /><span /></div>
                        <div><b /><span /></div>
                        <div><b /><span /></div>
                      </div>
                    )}
                    {activeCard.key === 'backend' && <div className="pv-nodes"><i /><i /><i /></div>}
                    {activeCard.key === 'api' && <div className="pv-api"><span /><span /><span /></div>}
                    {activeCard.key === 'agents' && (
                      <div className="pv-sk pv-sk-agents">
                        <div><b /><span /></div>
                        <div><b /><span /></div>
                        <div><b /><span /></div>
                      </div>
                    )}
                    {activeCard.key === 'source' && (
                      <div className="pv-sk pv-sk-source">
                        <aside><span /><span /><span /><span /><span /></aside>
                        <main><span /><span /><span /><span /></main>
                      </div>
                    )}
                    {activeCard.key === 'mobile' && (
                      <div className="pv-sk pv-sk-mobile"><div><i /><span /><span /></div></div>
                    )}
                    {activeCard.key === 'gateway' && (
                      <div className="pv-sk pv-sk-gateway">
                        <div><span /><span /></div>
                        <b />
                        <div><span /><span /></div>
                      </div>
                    )}
                    {activeCard.key === 'preview' && <div className="pv-preview"><span /></div>}
                    {activeCard.key === 'ui' && <div className="pv-ui"><span /><span /><span /><span /></div>}
                  </div>

                  <div className="pv-copy" key={`${activeCard.key}-copy`}>
                    <h3>{activeCard.title}</h3>
                    <p>{activeCard.body}</p>
                  </div>

                  <div className="pv-nav">
                    <button type="button" className="pv-arrow" aria-label="Previous note" onClick={() => showCard((cardIndex - 1 + cards.length) % cards.length)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 6 9 12l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                    <ul className="pv-dots">
                      {cards.map((card, index) => (
                        <li key={card.key}>
                          <button
                            type="button"
                            aria-label={`Show ${card.title}`}
                            aria-current={index === cardIndex ? 'true' : undefined}
                            onClick={() => showCard(index)}
                          />
                        </li>
                      ))}
                    </ul>
                    <button type="button" className="pv-arrow" aria-label="Next note" onClick={() => showCard((cardIndex + 1) % cards.length)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                  </div>
                </section>
              )}
            </div>

            {highlightBox && session.status === 'ready' && (
              <div
                className="highlight-rectangle"
                style={{
                  left: iframeRect ? `${iframeRect.left + highlightBox.x - 10}px` : `${highlightBox.x - 10}px`,
                  top: iframeRect ? `${iframeRect.top + highlightBox.y - 5}px` : `${highlightBox.y - 5}px`,
                  width: `${highlightBox.width + 20}px`,
                  height: `${highlightBox.height + 12}px`,
                  display: 'block',
                  visibility: 'visible',
                }}
              >
                <div className="highlight-label">{highlightBox.elementType || 'element'}</div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default IframeComponent;
